import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

interface RelatedPersonsProps {
  results: Record<string, any[]>;
  searchQuery?: string;
}

export function RelatedPersons({ results, searchQuery }: RelatedPersonsProps) {
  const [copiedText, setCopiedText] = useState<string | null>(null);
  const [isCollapsed, setIsCollapsed] = useState(false);

  // Функция для копирования текста
  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedText(text);
      setTimeout(() => setCopiedText(null), 2000); // Убираем подсветку через 2 секунды
    } catch (err) {
      console.error('Ошибка при копирования:', err);
    }
  };

  // Извлекаем фамилию из поискового запроса
  const getSearchLastName = () => {
    if (!searchQuery) return null;
    
    // Убираем дату и берем первое слово как фамилию
    const cleanQuery = searchQuery.replace(/\d{2}\.\d{2}\.\d{4}/g, '').trim();
    const words = cleanQuery.split(/\s+/);
    const lastName = words[0]?.toUpperCase() || null;
    return lastName;
  };

  // Проверяем, совпадает ли фамилия (по корню, игнорируя окончания)
  const hasMatchingLastName = (personName: string) => {
    const searchLastName = getSearchLastName();
    if (!searchLastName) return false;
    
    const personLastName = personName.split(/\s+/)[0]?.toUpperCase();
    if (!personLastName) return false;
    
    // Получаем корень фамилии (убираем типичные окончания)
    const getLastNameRoot = (name: string) => {
      // Убираем типичные окончания: -ОВ, -ОВА, -ИН, -ИНА, -СК, -СКАЯ, -ЕВ, -ЕВА, -ИЧ, -НА и т.д.
      return name
        .replace(/(ОВА?|ИНА?|СКАЯ?|СК|ЕВА?|ИЧ|НА|АЯ|ЫЙ)$/, '')
        .replace(/(ЕНК|УК|ЮК|ЯК|АК|ИК|ОК|УШ|ЮШ|ЯШ|АШ|ИШ|ОШ)$/, '');
    };
    
    const searchRoot = getLastNameRoot(searchLastName);
    const personRoot = getLastNameRoot(personLastName);
    
    const matches = searchRoot.length >= 3 && personRoot.startsWith(searchRoot);
    return matches;
  };

  // Извлекаем связанных лиц и информацию об источниках
  const getRelatedPersonsData = () => {
    const relatedPersons = new Set<string>();
    const sourcesWithRelations = new Map<string, number>();
    
    for (const [source, items] of Object.entries(results)) {
      let sourceRelationsCount = 0;
      
      for (const item of items) {
        // Ищем поле "Связь по адресу"
        const relationshipField = item['Связь по адресу'];
        if (relationshipField) {
          // Разделяем по запятым и точкам с запятой
          const persons = String(relationshipField)
            .split(/[,;]/)
            .map(person => person.trim())
            .filter(person => person.length > 0);
          
          persons.forEach(person => {
            relatedPersons.add(person);
          });
          
          sourceRelationsCount += persons.length;
        }
      }
      
      if (sourceRelationsCount > 0) {
        sourcesWithRelations.set(source, sourceRelationsCount);
      }
    }
    
    return {
      persons: Array.from(relatedPersons),
      sources: sourcesWithRelations
    };
  };

  const { persons: relatedPersons, sources } = getRelatedPersonsData();

  // Не показываем компонент, если нет связанных лиц
  if (relatedPersons.length === 0) {
    return null;
  }

  // Словарь названий источников
  const sourceNames: Record<string, string> = {
    'ruton': 'ВЗЛОМ RUTON',
    'rf-relatives-2023': 'РФ РОДСТВЕННИКИ 2023 D3',
    'relatives-2023': 'РФ РОДСТВЕННИКИ 2023 D3',
    'address-relations-2024': 'ВОЗМОЖНЫЕ СВЯЗИ ПО АДРЕСУ 2024',
    'relation-1': 'РФ РОДСТВЕННИКИ 2023 D3',
    'relation-3': 'РФ РОДСТВЕННИКИ 2023 D3',
    // Добавляем другие источники по мере необходимости
  };

  return (
    <Card className="mb-6">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg">Связанные лица ({relatedPersons.length})</CardTitle>
          <button
            onClick={() => setIsCollapsed(!isCollapsed)}
            className="text-muted-foreground hover:text-foreground transition-colors"
            title={isCollapsed ? "Развернуть" : "Свернуть"}
          >
            {isCollapsed ? "▼" : "▲"}
          </button>
        </div>
      </CardHeader>
      {!isCollapsed && (
        <CardContent>
          <div className="space-y-1">
            {relatedPersons.map((person, index) => {
              const personText = String(person);
              const isMatchingLastName = hasMatchingLastName(personText);
              return (
                <p 
                  key={index} 
                  className={`text-sm cursor-pointer hover:bg-muted/50 px-2 py-1 rounded transition-colors ${
                    copiedText === personText 
                      ? 'bg-green-100 text-green-800' 
                      : isMatchingLastName 
                        ? 'text-green-700 font-bold bg-green-50' 
                        : ''
                  }`}
                  onClick={() => copyToClipboard(personText)}
                  title="Нажмите для копирования"
                >
                  {personText}
                </p>
              );
            })}
          </div>

          {/* Источники связей */}
          {sources.size > 0 && (
            <div className="mt-4 pt-4 border-t">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <h4 className="font-medium text-sm text-muted-foreground mb-2">Источник связи</h4>
                  {Array.from(sources.entries()).map(([source, count]) => (
                    <div key={source} className="text-sm font-medium text-foreground">
                      {sourceNames[source] || source.toUpperCase()}
                    </div>
                  ))}
                </div>
                <div>
                  <h4 className="font-medium text-sm text-muted-foreground mb-2">Количество связей</h4>
                  {Array.from(sources.entries()).map(([source, count]) => (
                    <div key={source} className="text-sm">
                      <div className="font-medium text-foreground">{count} связанных лиц</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </CardContent>
      )}
    </Card>
  );
}
